import React from "react";
import {
  Typography,
  Box,
  Paper,
  Stack,
  LinearProgress,
  Skeleton,
  Chip,
} from "@mui/material";
import dayjs from "dayjs";
import { api, type ApiJar } from "../services/api";

function formatCurrency(cents: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
    cents / 100
  );
}

export default function GoalsPage() {
  const [jars, setJars] = React.useState<ApiJar[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { jars } = await api.getJars();
        if (!cancelled) setJars(jars);
      } catch (err) {
        console.error("Failed to load jars:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 900 }}>
        <Skeleton variant="text" width={200} height={40} />
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={100} sx={{ mt: 2 }} />
        ))}
      </Box>
    );
  }

  // Separate jars into those with a target (goals) and general-purpose jars
  const goals = jars.filter((j) => j.targetAmountCents > 0);
  const otherJars = jars.filter((j) => j.targetAmountCents <= 0);

  return (
    <Box sx={{ p: { xs: 1, sm: 3 }, maxWidth: 900 }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
        Goals
      </Typography>

      {goals.length === 0 && otherJars.length === 0 && (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No jars or goals yet. Create a shared jar to start tracking a
            savings goal.
          </Typography>
        </Paper>
      )}

      {goals.length > 0 && (
        <Stack spacing={2} sx={{ mb: 4 }}>
          {goals.map((jar) => {
            const progress = Math.min(
              (jar.currentAmountCents / jar.targetAmountCents) * 100,
              100
            );
            const daysLeft = jar.targetDate
              ? dayjs(jar.targetDate).diff(dayjs(), "day")
              : null;

            return (
              <Paper key={jar.id} sx={{ p: 2.5 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {jar.icon} {jar.name}
                    </Typography>
                    {jar.description && (
                      <Typography variant="caption" color="text.secondary">
                        {jar.description}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {daysLeft !== null && daysLeft >= 0 && (
                      <Chip
                        label={`${daysLeft}d left`}
                        size="small"
                        color={daysLeft < 30 ? "warning" : "default"}
                      />
                    )}
                    {daysLeft !== null && daysLeft < 0 && (
                      <Chip label="Overdue" size="small" color="error" />
                    )}
                  </Stack>
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ height: 10, borderRadius: 5, mb: 1 }}
                  color={progress >= 100 ? "success" : "primary"}
                />

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(jar.currentAmountCents, jar.currency)} of{" "}
                    {formatCurrency(jar.targetAmountCents, jar.currency)}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {progress.toFixed(0)}%
                  </Typography>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {otherJars.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Other Jars
          </Typography>
          <Stack spacing={2}>
            {otherJars.map((jar) => (
              <Paper key={jar.id} sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {jar.icon} {jar.name}
                    </Typography>
                    {jar.description && (
                      <Typography variant="caption" color="text.secondary">
                        {jar.description}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(jar.currentAmountCents, jar.currency)}
                  </Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}
